// noinspection SpellCheckingInspection

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import ReactTooltip from 'react-tooltip';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import isEmpty from 'lodash.isempty';

import { Col, Div, Icon, Row, Switch, Tag, Text } from '@pinpoint/design-kit';
import { charts } from '@pinpoint/onpoint-charts';

import { all, formatNumber, getModelTagStyles, identity, noOp } from 'src/functions';
import DefaultTheme from 'src/styles/DefaultTheme';
import { Button } from 'src/components';
import CONSTANTS from 'src/store/constants';
import { ModelProp } from 'src/store/typings';
import { CellRegulatoryTag } from '../../cells';

const {
  colors: { shade99, tagActuarial, ...rest },
} = DefaultTheme;

function extractNum(data) {
  let num = null;
  if (typeof num === 'string') num = Number(num);
  else if (typeof num === 'object') num = Number(data.value);
  return num;
}

const formatNumberLabelLong = (data) => {
  const num = extractNum(data);
  if (!num || !Number.isFinite(num)) return Number.NaN.toString();
  return num.toFixed(3);
};

const formatNumberLabel = (data) => {
  const num = extractNum(data);
  if (!num || !Number.isFinite(num)) return Number.NaN.toString();
  return num.toFixed(2);
};

const formatPercLabel = (data) => {
  const num = extractNum(data);
  if (!num || !Number.isFinite(num)) return Number.NaN.toString();
  return `${num.toFixed(1)}%`;
};

const formatDollarLabel = (data) => {
  const num = extractNum(data);
  if (!num || !Number.isFinite(num)) return Number.NaN.toString();
  return `$${formatNumber(Math.round(num))}`;
};

function liftDiffFromMean(lift) {
  return lift >= 1 || lift === 0 ? lift : -1 / lift;
}

const seriesLineStyles = ['3px solid', '3px dashed', '3px dotted'];
const seriesSymbol = ['circle', 'diamond', 'triangle'];

export default function ActuarialMode({
  altChart,
  modelsCompare: {
    models,
    quantiles,
    quantileIndices,
    liftScores,
    equalType,
    availableQuantiles: { current },
  },
  setQuantiles,
}) {
  const legacyActuarialModels = useMemo(
    () => models.map((mdl) => ['LIFT_VERSION_1_ACT', null, undefined].includes(mdl?.payload?.version)),
    [models]
  );
  // cat A (different actuals) if true, else B (same actuals)
  const sameActuals = useMemo(() => {
    const actualTypes = models.map(({ dataModelType }) => (dataModelType.includes('LOSS') ? 'LOSS' : dataModelType));
    return actualTypes.every((actual) => actual === actualTypes[0]);
  }, [models]);

  const toggleInfo = useMemo(() => {
    if (models && models?.length > 0) {
      return CONSTANTS.TAL_MODEL.ACT_STYLING[models[0].dataModelType];
    }
    // NOTE: default
    return CONSTANTS.TAL_MODEL.ACT_STYLING.ACT_FREQUENCY;
  }, [models]);
  const {
    COLOR: chartColor,
    SHOW_ACTUAL: showActualProp,
    HEADER_NAME: headerName,
    DISPLAY_DOLLAR: displayDollar,
  } = toggleInfo;

  // Chart data extracted using guarded calls to avoid passing nulls or undefined
  const selectedLiftScores = useMemo(
    () => liftScores.map((lftScs, idx) => lftScs[quantileIndices[idx]]),
    [liftScores, quantileIndices]
  );

  const actualLifts = useMemo(
    () => selectedLiftScores.map((data) => data?.p_actual_lift).filter(identity) || [],
    [selectedLiftScores]
  );
  const actualValues = useMemo(
    () => selectedLiftScores.map((data) => data?.p_actual.filter(identity)) || [],
    [selectedLiftScores]
  );
  const ratioLifts = useMemo(
    () => selectedLiftScores.map((data) => data?.p_lift.filter(identity)) || [],
    [selectedLiftScores]
  );
  const ratioValues = useMemo(
    () => selectedLiftScores.map((data) => data?.p_loss_ratio.filter(identity)) || [],
    [selectedLiftScores]
  );
  const diffRatioLifts = useMemo(
    () => ratioLifts.map((data) => (data?.map ? data.map(liftDiffFromMean) : [])) || [],
    [ratioLifts]
  );
  const diffActualLifts = useMemo(
    () => actualLifts.map((data) => (data?.map ? data.map(liftDiffFromMean) : [])) || [],
    [actualLifts]
  );
  const disableActual = useMemo(() => isEmpty(actualValues) || !sameActuals, [actualValues, sameActuals]);

  // Toggle Button Initial State
  const [showActual, setShowActual] = useState(showActualProp && !disableActual);
  const [showLift, setShowLift] = useState(altChart);

  // Toggle Button Handlers
  const toggleActual = useCallback(() => setShowActual(!showActual), [showActual]);
  const toggleLift = useCallback(() => setShowLift(!showLift), [showLift]);

  const datumFormatter = useMemo(() => {
    if (showActual && displayDollar && !showLift) return formatDollarLabel; // Actual + Value (severity and loss cost)
    if (!showActual && !showLift) return formatPercLabel; // Ratio + Value (all models)
    if (showLift) return formatNumberLabel; // Actual + Lift, Ratio + Lift (all models)
    return formatNumberLabelLong; // Actual + Value (frequency)
  }, [showLift, showActual, displayDollar]);

  const tooltipFormatter = useCallback(
    ({ dataIndex, seriesIndex }) => {
      const replacePattern = 'YX average';
      const replaceValue = showActual
        ? get(diffActualLifts, `[${seriesIndex}][${dataIndex}]`, Number.NaN)
        : get(diffRatioLifts, `[${seriesIndex}][${dataIndex}]`, Number.NaN);
      return showLift ? replacePattern.replace('Y', replaceValue.toFixed(2)) : null;
    },
    [showActual, diffActualLifts, diffRatioLifts, showLift]
  );

  const yAxisFormatter = useCallback(
    (num) => {
      if (displayDollar && showActual && !showLift) return `$${formatNumber(num)}`;
      return num;
    },
    [showActual, showLift, displayDollar]
  );

  const pinpointData = useMemo(() => {
    if (showActual) return showLift ? actualLifts : actualValues;
    return showLift ? ratioLifts : ratioValues;
  }, [actualLifts, actualValues, ratioLifts, ratioValues, showActual, showLift]);

  const legendInfo = useMemo(
    () =>
      pinpointData?.length
        ? models.map(({ dataModelType, name: modelName, modelData }, idx) => ({
            ...getModelTagStyles(dataModelType, ''),
            ...CONSTANTS.TAL_MODEL.ACT_STYLING[dataModelType],
            dataModelType,
            bucketSpread: ((pinpointData?.[idx]?.at(-1) ?? 1) / (pinpointData?.[idx]?.[0] ?? 1)).toFixed(1),
            modelName,
            isRemediated: modelData?.metadataPayload?.fairness ?? false,
          }))
        : [],
    [models, pinpointData]
  );

  const seriesData = useMemo(
    () =>
      pinpointData?.length
        ? legendInfo.map(({ modelName, COLOR }, idx) => ({
            name: modelName,
            label: {
              show: true,
              position: 'top',
              color: rest[COLOR],
              fontWeight: 500,
              fontSize: 18,
              formatter: datumFormatter,
            },
            data: pinpointData[idx],
            values: pinpointData[idx],
            symbolSize: 8,
            type: 'line',
            color: rest[COLOR],
            symbol: seriesSymbol[idx],
            lineStyle: {
              color: rest[COLOR],
              width: seriesLineStyles[idx][0], // first char in the string
              type: seriesLineStyles[idx].split(' ').at(-1), // split on space, get last entry
            },
            itemStyle: { color: 'white', borderColor: rest[COLOR], width: 6 },
            markLine: undefined,
            areaStyle: undefined,
          }))
        : [],
    [datumFormatter, pinpointData, legendInfo]
  );

  const yAxisName = useMemo(() => {
    let name = 'Actual ';
    if (showActual) name += `${headerName}`.toLowerCase();
    else name += `client loss ratio`;
    if (showLift) name += ' relativities';
    if (!showActual && !showLift) name += 's';
    return name;
  }, [showActual, showLift, headerName]);

  const options = useMemo(
    () => ({
      grid: {
        containLabel: true,
        right: 0,
        left: 0,
        bottom: 0,
      },
      legend: { show: false },
      backgroundColor: false,
      yAxis: {
        show: true,
        name: null,
        axisLabel: {
          fontSize: 14,
          color: shade99,
          fontWeight: 500,
          formatter: yAxisFormatter,
        },
        axisLine: { show: false },
        splitLine: { show: true },
        axisTick: { show: false },
        scale: true,
        splitNumber: 5,
        type: 'value',
      },
      tooltip: {
        formatter: tooltipFormatter,
      },
      xAxis: {
        show: true,
        boundaryGap: ['20%', '20%'],
        minInterval: 1,
        name: null,
        axisLabel: { fontSize: 14, color: shade99, fontWeight: 500 },
        splitLine: { show: false },
        axisLine: { show: true, width: 2, color: shade99 },
        axisTick: { show: false },
        type: 'category',
      },
    }),
    [yAxisFormatter, tooltipFormatter]
  );

  useEffect(() => {
    setShowActual(showActualProp && !disableActual);
  }, [showActualProp, disableActual]);

  useLayoutEffect(() => {
    ReactTooltip.rebuild();
  });

  return (
    <Div h="100%" w="100%" maxH="100%" p={{ x: '1.5rem', y: '1rem' }} d="flex" flexDir="column">
      <Row data-test="title-row" flexWrap="nowrap" p={{ t: '.5rem' }}>
        <Col data-test="title-btns-col" gutterWidth="0" d="flex" align="flex-start" justify="flex-start">
          <Text textSize="title" textColor="tabSelected" style={{ fontWeight: 400 }} p={{ l: '3rem', r: '1.5rem' }}>
            Lift Calculations
          </Text>
          {current.map((val) => (
            <Button
              buttonStyle="tab"
              key={`bucket-${val}`}
              selected={quantiles === val}
              p={{ x: '1.5em' }}
              w="1.875rem"
              data-tip={CONSTANTS.TAL_MODEL.QUANTILE_LABEL[val]}
              onClick={() => {
                setQuantiles(val);
              }}
            >
              <Text>{val}</Text>
            </Button>
          ))}
        </Col>
        <Col data-test="title-legend-col" gutterWidth="0">
          {legendInfo.map(
            ({ name, viewBox, tip, dataModelType, COLOR, bucketSpread, modelName, isRemediated }, idx) => (
              <Div
                key={`${name}-legend-div`}
                d="flex"
                p={idx !== 0 ? { t: '.2rem', l: '4rem' } : { l: '4rem' }}
                justify="flex-start"
                align="flex-start"
                textSize="subheader"
              >
                <Div w="3rem" m={{ r: '1rem' }} alignSelf="center">
                  <Div
                    w="100%"
                    h="100%"
                    borderColor={COLOR}
                    border={{ b: seriesLineStyles[idx] }}
                    m={{ b: `${+seriesLineStyles[idx][0]}px`, t: idx !== 0 ? '.15rem' : '0' }}
                  />
                </Div>
                <Tag
                  align="center"
                  p="0px"
                  h="2rem"
                  m={{ t: idx !== 0 ? '.15rem' : '0', r: '.5rem' }}
                  whiteSpace="nowrap"
                  border="1px solid"
                  textColor={COLOR}
                  borderColor={COLOR}
                  data-tip={tip}
                  prefix={
                    <Div h="100%" d="flex" align="center" p=".375rem">
                      <Icon name={name} size="1.25rem" viewBox={viewBox} color={COLOR} />
                    </Div>
                  }
                  suffix={
                    isRemediated ? (
                      <Div h="100%" d="flex" align="center" p={{ y: '.375rem' }} bg={COLOR}>
                        <Icon name="Fairness" size="1.25rem" viewBox="0 0 37 36" color="white" m={{ x: '.375rem' }} />
                      </Div>
                    ) : undefined
                  }
                >
                  <Text textSize="subheader" m={{ x: '.375rem' }}>
                    {CONSTANTS.SEGMENT.TAG[dataModelType] || CONSTANTS.TAL_MODEL.TAG_NAME[dataModelType]}
                  </Text>
                </Tag>
                {models?.[idx]?.framework && (
                  <CellRegulatoryTag
                    h="2rem"
                    m={{ t: idx !== 0 ? '.15rem' : '0' }}
                    textSize="subheader"
                    iconProps={{ size: '1.25rem' }}
                    value={{ framework: models?.[idx]?.framework }}
                  />
                )}
                <Text
                  textColor={COLOR}
                  style={{ whiteSpace: 'nowrap', alignSelf: 'center' }}
                  textWeight="500"
                  m={{ l: '.5rem', t: idx !== 0 ? '.15rem' : '0' }}
                >
                  ({bucketSpread}x spread)
                </Text>
                <Text
                  textColor={COLOR}
                  m={{ l: '.5rem', t: idx !== 0 ? '.15rem' : '0' }}
                  style={{ whiteSpace: 'nowrap', alignSelf: 'center' }}
                >
                  {modelName.replace(/(LOSS COST.*:|FREQUENCY:|SEVERITY:)\s*/, '').replace(' (REMEDIATED)', '')}
                </Text>
              </Div>
            )
          )}
        </Col>
      </Row>
      <Row data-test="chart-row" p={{ t: '.5rem' }} m={{ r: '2rem' }} flexGrow="1" flexDir="column" flexWrap="nowrap">
        <Col data-test="chart-content-col" gutterWidth="0" align="stretch" justify="center" d="flex" size="12">
          <Row flexGrow="1">
            <Col
              data-debug="yaxis-col"
              gutterWidth="0"
              transform="rotate(180deg)"
              style={{ writingMode: 'vertical-lr', fontSize: '18px' }}
              d="flex"
              align="center"
              justify="center"
              textColor="shade99"
            >
              <Div tag="span" m={{ r: '2rem' }} p={{ l: '1rem' }}>
                {yAxisName}
              </Div>
            </Col>
            <Col flexGrow="1">
              {seriesData?.length && options && (
                <charts.Line
                  overflow="hidden"
                  usePalette={false}
                  showMarkline={false}
                  showGradients={false}
                  labels={[...Array(quantiles || 10).keys()].map((k) => k + 1)}
                  series={seriesData}
                  options={options}
                />
              )}
            </Col>
          </Row>
        </Col>
      </Row>
      <Row data-debug="spacer2" m={{ x: '0rem', b: '.5rem' }} p={{ x: '2rem' }}>
        <Col
          size={12}
          gutterWidth="0"
          data-debug="xaxis--col"
          justify="center"
          align="top"
          d="flex"
          textColor="shade99"
          style={{ fontSize: '18px' }}
        >
          <Div tag="span" p={{ y: '1rem' }}>
            Ranking percentiles with equal {all(legacyActuarialModels) ? 'premiums' : `${equalType}`}
          </Div>
        </Col>
        <Col
          size={12}
          gutterWidth="0"
          data-debug="left-toggles-col"
          borderColor="shadeE6"
          border={{ t: '1px solid' }}
        />
      </Row>
      <Row data-test="toggle-row" p={{ t: '.5rem' }} flexGrow="0" flexDir="column" flexWrap="nowrap">
        <Col data-test="chart-toggle-col" gutterWidth="0" align="center" justify="center" d="flex">
          <Row p={{ b: '.5rem' }} w="100%">
            {sameActuals && (
              <Col
                size={6}
                data-debug="left-toggle-col"
                gutterWidth="6rem"
                d="flex"
                justify="left"
                align="baseline"
                flexWrap="nowrap"
              >
                <Text
                  textSize="subtitle"
                  h="1.125rem"
                  textColor={disableActual || showActual ? 'shade99' : chartColor}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  Actuals: Loss ratio
                </Text>
                <Div disabled alignSelf="center" m={{ x: '1rem' }} onClick={disableActual ? noOp : toggleActual}>
                  <Switch
                    m="0"
                    checked={showActual}
                    activeColor={disableActual ? 'shade99' : chartColor}
                    inactiveColor={disableActual ? 'shade99' : chartColor}
                    activeShadow="0"
                    inactiveShadow="0"
                  />
                </Div>
                <Text
                  h="1.125rem"
                  textSize="subtitle"
                  textColor={disableActual || !showActual ? 'shade99' : chartColor}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  {headerName}
                </Text>
              </Col>
            )}
            <Col
              offset={sameActuals ? 0 : 6}
              size={6}
              data-debug="right-toggles-col"
              d="flex"
              justify="right"
              align="baseline"
              flexWrap="nowrap"
              gutterWidth="6rem"
            >
              <Text
                textSize="subtitle"
                h="1.125rem"
                textColor={showLift ? 'shade99' : chartColor}
                style={{ whiteSpace: 'nowrap' }}
              >
                Values
              </Text>
              <Div alignSelf="center" m={{ x: '1rem' }} onClick={toggleLift}>
                <Switch
                  m="0"
                  checked={showLift}
                  activeColor={chartColor}
                  inactiveColor={chartColor}
                  activeShadow="0"
                  inactiveShadow="0"
                />
              </Div>
              <Text
                textSize="subtitle"
                h="1.125rem"
                textColor={showLift ? chartColor : 'shade99'}
                style={{ whiteSpace: 'nowrap' }}
              >
                Lift
              </Text>
            </Col>
          </Row>
        </Col>
      </Row>
    </Div>
  );
}

ActuarialMode.propTypes = {
  altChart: PropTypes.bool.isRequired,
  setQuantiles: PropTypes.func.isRequired,
  modelsCompare: PropTypes.shape({
    equalType: PropTypes.string.isRequired,
    availableQuantiles: PropTypes.shape({ current: PropTypes.arrayOf(PropTypes.number) }).isRequired,
    quantiles: PropTypes.number.isRequired,
    quantileIndices: PropTypes.arrayOf(PropTypes.number).isRequired,
    liftScores: PropTypes.arrayOf(
      PropTypes.arrayOf(
        PropTypes.shape({
          p_lift: PropTypes.arrayOf(PropTypes.number),
          p_actual_lift: PropTypes.arrayOf(PropTypes.number),
          p_actual: PropTypes.arrayOf(PropTypes.number),
          p_loss_ratio: PropTypes.arrayOf(PropTypes.number),
        })
      )
    ).isRequired,
    models: PropTypes.arrayOf(ModelProp).isRequired,
    fetchModelsStatus: PropTypes.oneOf([PropTypes.string, null]).isRequired,
    chart: PropTypes.string,
  }).isRequired,
};
